export const TOOL = Object.freeze({
  SELECT: 'Select Tool',
  ROOF: 'Roof Tool',
  OBSTRUCTION: 'Roof Obstruction Tool',
  PROPERTY_LINE: 'Property Line Tool',
  FIRE_SETBACK: 'Fire Setback Tool',
  SOLAR_PANEL: '+Solar Panel Tool',
  MAP: 'Map Tool',
  EXPORT_DXF: 'Export DXF Tool',
  LAYER: 'Layer Tool',
});

export const OBSTRUCTION_MODE = Object.freeze({
  CIRCLE: 'circle',
  RECTANGLE: 'rectangle',
  POLYGON: 'polygon',
});

export const PANEL_ORIENTATION = Object.freeze({
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
});

export const LAYERS = [
  { key: 'map', label: 'Map Overlay' },
  { key: 'roofs', label: 'Roofs' },
  { key: 'rafters', label: 'Rafters' },
  { key: 'propertyLines', label: 'Property Lines' },
  { key: 'obstructions', label: 'Obstructions' },
  { key: 'fireSetbacks', label: 'Fire Setbacks' },
  { key: 'solarPanels', label: 'Solar Panels' },
  { key: 'attachments', label: 'Attachments' },
  { key: 'junctionBoxes', label: 'Junction Box', defaultVisible: false },
  { key: 'groundLugs', label: 'Ground Lugs', defaultVisible: false },
  { key: 'midClamps', label: 'Mid Clamps', defaultVisible: false },
  { key: 'endClamps', label: 'End Clamps', defaultVisible: false },
];

// Solar panel data is intentionally NOT hardcoded here.
// The source of truth is Supabase: public.solar_panels.
// This is only the preferred default model name. The panel row still must exist in Supabase.
export const DEFAULT_SOLAR_PANEL_MODEL_NAME = 'SEG-440-BTD-BG';

let activeSolarPanelModels = [];

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function normalizeSolarPanelModel(row) {
  if (!row) return null;

  const modelName = String(row.name || row.model || '').trim();
  const manufacturer = String(row.manufacturer || '').trim();
  const watts = toNumber(row.watts, 0);
  const widthIn = toNumber(row.widthIn ?? row.width_in, 0);
  const heightIn = toNumber(row.heightIn ?? row.height_in, 0);
  const thicknessInRaw = row.thicknessIn ?? row.thickness_in;
  const thicknessIn = thicknessInRaw === null || thicknessInRaw === undefined || thicknessInRaw === ''
    ? null
    : toNumber(thicknessInRaw, null);

  if (!modelName || watts <= 0 || widthIn <= 0 || heightIn <= 0) return null;

  return {
    id: row.id || modelName,
    manufacturer,
    model: modelName,
    name: modelName,
    watts,
    widthIn,
    heightIn,
    thicknessIn,
    active: row.active !== false,
  };
}

export function setSolarPanelModelsFromDatabase(rows = []) {
  activeSolarPanelModels = (rows || [])
    .map(normalizeSolarPanelModel)
    .filter(Boolean)
    .filter((panel) => panel.active !== false);

  return getSolarPanelModels();
}

export function getSolarPanelModels() {
  return activeSolarPanelModels.map((model) => ({ ...model }));
}

export function getDefaultPanelModel() {
  return activeSolarPanelModels.find((model) => model.name === DEFAULT_SOLAR_PANEL_MODEL_NAME || model.model === DEFAULT_SOLAR_PANEL_MODEL_NAME)
    || activeSolarPanelModels[0]
    || null;
}

export function getPanelModel(name) {
  if (name && typeof name === 'object') {
    return normalizeSolarPanelModel(name);
  }

  const requestedName = String(name || '').trim();

  if (!requestedName) return getDefaultPanelModel();

  return activeSolarPanelModels.find((model) => model.name === requestedName || model.model === requestedName) || null;
}

export function getProjectedPanelSize(modelName, orientation, roofPitch = 0) {
  const model = getPanelModel(modelName);

  if (!model) {
    return {
      widthIn: 0,
      heightIn: 0,
      watts: 0,
      model: null,
    };
  }

  const pitchDegrees = Math.max(0, Math.min(89, Number(roofPitch) || 0));
  const pitchAngle = (pitchDegrees * Math.PI) / 180;
  const projection = Math.max(0.72, Math.cos(pitchAngle));
  const width = orientation === PANEL_ORIENTATION.PORTRAIT ? model.widthIn : model.heightIn;
  const height = orientation === PANEL_ORIENTATION.PORTRAIT ? model.heightIn : model.widthIn;

  return {
    widthIn: width,
    heightIn: height * projection,
    watts: model.watts,
    model,
  };
}

export function defaultLayers() {
  return LAYERS.reduce((acc, layer) => {
    acc[layer.key] = { visible: layer.defaultVisible !== false, locked: false };
    return acc;
  }, {});
}

export function layerIsVisible(layers, key) {
  return layers?.[key]?.visible !== false;
}

export function layerIsLocked(layers, key) {
  return layers?.[key]?.locked === true;
}
